import PropTypes from "prop-types";
import { PureComponent } from "react";
import ResizeObserver from "resize-observer-polyfill";

const propTypes = {
  // This property is used to re-apply the classes, for example when the className of the observed node changes.
  forceUpdate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  maxHeightBrkpts: PropTypes.objectOf(PropTypes.number),
  maxWidthBrkpts: PropTypes.objectOf(PropTypes.number),
  observe: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Element),
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  onResize: PropTypes.func,

  stylePropHeight: PropTypes.string,
};

// Same as bootstrap
const defaultProps = {
  forceUpdate: null,
  maxHeightBrkpts: {
    /* eslint-disable */
    xs: 576,
    s: 768,
    m: 992,
    l: 1200,
    xl: Infinity,
    /* eslint-enable*/
  },
  maxWidthBrkpts: {
    /* eslint-disable */
    xs: 576,
    s: 768,
    m: 992,
    l: 1200,
    xl: Infinity,
    /* eslint-enable */
  },
  observe: null,
  onResize: null,
  stylePropHeight: null,
};

/**
 * This component adds css class to an element depending on his size.
 */
class ResizeHandler extends PureComponent {
  constructor(props) {
    super(props);
    this.observer = new ResizeObserver((entries) => this.onResize(entries));
    this.nodes = [];
  }

  static applyBreakpoints(entry, breakpoints, size, direction) {
    let found = false;
    let screenSize;
    Object.entries(breakpoints).forEach((brkpt) => {
      const cssClass = `tm-${direction}-${brkpt[0]}`;
      entry.target.classList.remove(cssClass);
      if (!found && size <= brkpt[1]) {
        found = true;
        [screenSize] = brkpt;
        entry.target.classList.add(cssClass);
      }
    });
    return screenSize;
  }

  componentDidMount() {
    this.observe();
  }

  componentDidUpdate(prevProps) {
    const { forceUpdate, observe } = this.props;

    if (
      observe !== prevProps.observe ||
      forceUpdate !== prevProps.forceUpdate
    ) {
      this.observe();
    }
  }

  componentWillUnmount() {
    this.observer.disconnect();
  }

  observe() {
    this.observer.disconnect();
    const { observe } = this.props;

    if (!observe) {
      return;
    }

    if (typeof observe === "string" || observe instanceof String) {
      this.nodes = document.querySelectorAll(observe);
    } else if (observe instanceof Element) {
      this.nodes.push(observe);
    } else if (observe.current instanceof Element) {
      // observe value created with createRef() on a html node.
      this.nodes.push(observe.current);
    } else {
      // React 19 removed findDOMNode, so component instances are not supported.
      console.warn(
        "ResizeHandler: don't observe a component, use a querySelector or an HTML node instead.",
      );
    }

    if (this.nodes.length) {
      this.nodes.forEach((node) => this.observer.observe(node));
    }
  }

  onResize(entries) {
    const { maxHeightBrkpts, maxWidthBrkpts, onResize, stylePropHeight } =
      this.props;

    if (stylePropHeight) {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty(stylePropHeight, `${vh}px`);
    }

    if (!maxWidthBrkpts && !maxHeightBrkpts) {
      return;
    }

    let newScreenWidth;
    let newScreenHeight;

    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const rect = entry.contentRect;
      const { height, width } = rect;

      if (maxWidthBrkpts) {
        newScreenWidth = ResizeHandler.applyBreakpoints(
          entry,
          maxWidthBrkpts,
          width,
          "w",
        );
      }
      if (maxHeightBrkpts) {
        newScreenHeight = ResizeHandler.applyBreakpoints(
          entry,
          maxHeightBrkpts,
          height,
          "h",
        );
      }
    }

    if (onResize) {
      onResize(entries, newScreenWidth, newScreenHeight);
    }
  }

  render() {
    return null;
  }
}

ResizeHandler.propTypes = propTypes;
ResizeHandler.defaultProps = defaultProps;

export default ResizeHandler;
